import { useState } from "react";
import { FadeUp, scrollToId } from "@/components/motion";
import { track } from "@/lib/analytics";
import { UserRound, Linkedin, ArrowUpRight } from "lucide-react";

// Replaceable via the asset system: drop a file at /public/george.jpg
const PHOTO = "/george.jpg";
const LINKEDIN_URL = "https://au.linkedin.com/in/barrettgeorge?trk=profile-badge";

export default function MeetGeorge() {
  const [imgOk, setImgOk] = useState(true);
  return (
    <section data-testid="meet-george" id="about" className="relative bg-[#141414] py-28 md:py-40 border-y border-[#27272A]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
        <FadeUp className="lg:col-span-3">
          <div className="relative aspect-[4/5] w-full max-w-[240px] bg-[#0A0A0A] border border-[#27272A] overflow-hidden">
            {imgOk ? (
              <img
                src={PHOTO} alt="George Barrett, founder of Barrett Career Brand"
                onError={() => setImgOk(false)}
                className="w-full h-full object-cover" loading="lazy"
              />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center text-center p-8" data-testid="george-photo-placeholder">
                <UserRound size={56} className="text-[#27272A]" />
                <p className="mt-4 font-body text-sm text-[#52525B]">Photo placeholder — add <code className="text-[#A1A1AA]">/public/george.jpg</code></p>
              </div>
            )}
          </div>
        </FadeUp>

        <FadeUp delay={0.1} className="lg:col-span-9">
          <p className="tracking-label text-[11px] uppercase font-body font-bold text-[#DFFF00] mb-6">Meet George</p>
          <h2 className="font-display font-bold tracking-tighter leading-[1.02] text-3xl md:text-5xl text-[#F8F8F5]">
            Career experience, translated into a stronger <span className="font-serif-accent font-light">professional story.</span>
          </h2>
          <div className="mt-6 space-y-4 font-body text-base md:text-lg text-[#A1A1AA] leading-relaxed max-w-2xl">
            <p>
              I'm George Barrett, the founder of Barrett Career Brand. I bring more than 20 years of experience
              across community development, program management, stakeholder engagement and professional writing.
              I combine human judgement, lived experience and AI-supported analysis to help clients recognise and
              communicate their professional value.
            </p>
            <p>
              AI supports the process, but you remain the source of truth. Every recommendation is shaped around
              your experience, ambitions and the employment market you want to enter.
            </p>
          </div>
          <button
            data-testid="work-with-george"
            onClick={() => { track("consultation_started", { from: "about" }); scrollToId("#consultation"); }}
            className="mt-8 rounded-full bg-[#DFFF00] text-[#0A0A0A] font-body font-semibold px-8 py-4 hover:bg-[#C8E600] transition-colors duration-300"
          >
            Work with George
          </button>

          {/* On-brand LinkedIn card (replaces LinkedIn's official badge, which doesn't render inside SPAs) */}
          <a
            href={LINKEDIN_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="linkedin-card"
            onClick={() => track("linkedin_click", { from: "meet-george" })}
            className="mt-8 inline-flex items-center gap-4 border border-[#27272A] hover:border-[#DFFF00] px-5 py-4 group transition-colors duration-300"
          >
            <span className="w-11 h-11 rounded-full bg-[#0A66C2] flex items-center justify-center shrink-0">
              <Linkedin size={20} className="text-white" fill="currentColor" strokeWidth={0} />
            </span>
            <span className="flex flex-col">
              <span className="tracking-label text-[10px] uppercase font-body font-bold text-[#52525B]">Connect on LinkedIn</span>
              <span className="font-body text-sm text-[#F8F8F5] font-semibold flex items-center gap-1 group-hover:text-[#DFFF00] transition-colors">
                George Barrett <ArrowUpRight size={14} className="opacity-70" />
              </span>
            </span>
          </a>
        </FadeUp>
      </div>
    </section>
  );
}
